import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./CourseManager.css";

const backend = BACKEND_URL.replace(/\/+$/, "");
const api = `${backend}/course-manager-api.php`;

function emptyCourse() {
  return {
    id: "",
    folder: "",
    type: "paid",
    category: "",
    title: "",
    description: "",
    image: "",
    duration: "",
    price: "",
    navigation: {
      showTabs: false,
      showSidebar: true,
      tabs: [],
    },
  };
}

async function readResponse(response) {
  const data = await response.json();

  if (!response.ok || !data.ok) {
    throw new Error(data.message || "Unable to process request.");
  }

  return data;
}

export default function CourseManager() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [version, setVersion] = useState("");
  const [csrf, setCsrf] = useState("");

  const [course, setCourse] = useState(emptyCourse);
  const [originalId, setOriginalId] = useState("");
  const [newCategory, setNewCategory] = useState("");

  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [removeBanner, setRemoveBanner] = useState(false);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const fileInput = useRef(null);
  const categoryDialog = useRef(null);
  const [categoryError, setCategoryError] = useState("");
  async function load() {
    const response = await fetch(api, {
      credentials: "include",
      cache: "no-store",
    });

    const data = await readResponse(response);

    setCourses(data.courses);
    setCategories(data.categories);
    setVersion(data.version);
    setCsrf(data.csrf);
  }

  useEffect(() => {
    load()
      .catch((error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!banner) {
      setBannerPreview("");
      return;
    }

    const url = URL.createObjectURL(banner);
    setBannerPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [banner]);

  function openCourse(item = null) {
    const next = item
      ? {
          ...emptyCourse(),
          ...item,
          price: item.price ?? "",
          navigation: {
            showTabs: item.navigation?.showTabs === true,
            showSidebar: item.navigation?.showSidebar !== false,
            tabs: (item.navigation?.tabs || []).map((tab) => ({
              ...tab,
              folders: [...(tab.folders || [])],
            })),
          },
        }
      : emptyCourse();

    setCourse(next);
    setOriginalId(item ? String(item.id) : "");
    setBanner(null);
    setRemoveBanner(false);
    setMessage("");

    if (fileInput.current) fileInput.current.value = "";
  }

  function updateField(key, value) {
    setCourse((previous) => ({ ...previous, [key]: value }));
  }

  function updateNavigation(key, value) {
    setCourse((previous) => ({
      ...previous,
      navigation: {
        ...previous.navigation,
        [key]: value,
      },
    }));
  }

  function updateTab(index, key, value) {
    setCourse((previous) => ({
      ...previous,
      navigation: {
        ...previous.navigation,
        tabs: previous.navigation.tabs.map((tab, position) =>
          position === index ? { ...tab, [key]: value } : tab,
        ),
      },
    }));
  }

  function moveTab(index, direction) {
    const tabs = [...course.navigation.tabs];
    const target = index + direction;

    if (target < 0 || target >= tabs.length) return;

    [tabs[index], tabs[target]] = [tabs[target], tabs[index]];
    updateNavigation("tabs", tabs);
  }

  function payloadCourse() {
    return {
      ...course,
      id: course.id.trim(),
      folder: course.folder.trim(),
      title: course.title.trim(),
      description: course.description.trim(),
      duration: course.duration.trim(),
      price: course.price === "" ? null : Number(course.price),
      navigation: {
        ...course.navigation,
        tabs: course.navigation.tabs.map((tab) => ({
          id: tab.id.trim(),
          title: tab.title.trim(),
          folders: tab.folders.map((folder) => folder.trim()).filter(Boolean),
        })),
      },
    };
  }

  async function send(action) {
    if (busy) return;

    if (
      action === "delete" &&
      !window.confirm(
        "Delete this course entry? Its images and folders will remain.",
      )
    ) {
      return;
    }

    setBusy(true);
    setMessage("");

    try {
      if (action === "save" && banner?.size > 5 * 1024 * 1024) {
        throw new Error("Choose an image up to 5 MB.");
      }

      const body = new FormData();

      body.append(
        "payload",
        JSON.stringify({
          action,
          csrf,
          version,
          originalId,
          course: payloadCourse(),
          removeBanner,
        }),
      );

      if (action === "save" && banner) {
        body.append("banner", banner);
      }

      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        body,
      });

      await readResponse(response);
      await load();

      openCourse();
      setMessage(
        action === "delete"
          ? "Course deleted successfully."
          : "Course saved successfully.",
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function addCategory() {
    if (busy) return;

    const name = newCategory.trim();

    if (!name) {
      setCategoryError("Please enter a category name.");
      return;
    }

    setBusy(true);
    setCategoryError("");

    try {
      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "addCategory",
          name,
          csrf,
          version,
        }),
      });

      const data = await readResponse(response);
      const addedName = data.name || name;

      setCategories((previous) =>
        Array.isArray(data.categories)
          ? data.categories
          : [...new Set([...previous, addedName])],
      );

      // Keep the current course fields and select the new category.
      setCourse((previous) => ({
        ...previous,
        category: addedName,
      }));

      // Keep API metadata current when returned.
      if (data.version !== undefined) {
        setVersion(data.version);
      }

      if (data.csrf !== undefined) {
        setCsrf(data.csrf);
      }

      setNewCategory("");
      categoryDialog.current?.close();
      setMessage("Category added successfully.");
    } catch (error) {
      setCategoryError(error.message);
    } finally {
      setBusy(false);
    }
  }
  const image = banner
    ? bannerPreview
    : removeBanner || !course.image
      ? ""
      : `${backend}/${course.image
          .split("/")
          .map(encodeURIComponent)
          .join("/")}`;

  if (loading) {
    return <p className="cm-page">Loading course manager…</p>;
  }

  if (!csrf) {
    return (
      <p className="cm-page" role="alert">
        {message || "Unable to load course manager. Reload the page."}
      </p>
    );
  }

  return (
    <main className="cm-page">
      <h1>Course Manager</h1>
      <p>Add courses, upload banners, and manage navigation.</p>

      {message && (
        <p className="cm-message" role="status">
          {message}
        </p>
      )}

      <fieldset className="cm-fieldset" disabled={busy}>
        <div className="cm-layout">
          <aside className="cm-panel">
            <button type="button" onClick={() => openCourse()}>
              + Add Course
            </button>

            <div className="cm-course-list">
              {courses.length === 0 && <p>No courses added yet.</p>}

              {courses.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={String(item.id) === originalId ? "cm-active" : ""}
                  onClick={() => openCourse(item)}
                >
                  <strong>{item.title}</strong>
                  <small>
                    {item.id} · {item.category}
                  </small>
                </button>
              ))}
            </div>
          </aside>

          <section className="cm-panel">
            <h2>{originalId ? "Edit Course" : "Add Course"}</h2>

            <form
              onSubmit={(event) => {
                event.preventDefault();

                if (event.target !== event.currentTarget) return;

                send("save");
              }}
            >
              <div className="cm-grid">
                {[
                  ["id", "Course ID"],
                  ["folder", "Folder"],
                ].map(([key, label]) => (
                  <label key={key}>
                    {label}
                    <input
                      required
                      pattern="[A-Za-z0-9_-]+"
                      value={course[key]}
                      onChange={(event) => updateField(key, event.target.value)}
                    />
                  </label>
                ))}

                <label>
                  Course Type
                  <select
                    value={course.type}
                    onChange={(event) =>
                      updateField("type", event.target.value)
                    }
                  >
                    <option value="paid">Paid</option>
                    <option value="demo">Free Demo</option>
                  </select>
                </label>

                <div className="cm-category-field">
                  <div className="cm-category-heading">
                    <label htmlFor="cm-course-category">Category</label>

                    <button
                      type="button"
                      className="cm-category-add"
                      onClick={() => {
                        setNewCategory("");
                        setCategoryError("");
                        categoryDialog.current?.showModal();
                      }}
                    >
                      + Add Category
                    </button>
                  </div>

                  <select
                    id="cm-course-category"
                    required
                    value={course.category}
                    onChange={(event) =>
                      updateField("category", event.target.value)
                    }
                  >
                    <option value="">Select category</option>

                    {categories.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="cm-full cm-actions">
                  <input
                    aria-label="New category"
                    placeholder="New category name"
                    maxLength={100}
                    value={newCategory}
                    onChange={(event) => setNewCategory(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addCategory();
                      }
                    }}
                  />
                  <button type="button" onClick={addCategory}>
                    Add Category
                  </button>
                </div> */}

                <label className="cm-full">
                  Course Title
                  <input
                    required
                    value={course.title}
                    onChange={(event) =>
                      updateField("title", event.target.value)
                    }
                  />
                </label>

                <label>
                  Duration
                  <input
                    value={course.duration}
                    onChange={(event) =>
                      updateField("duration", event.target.value)
                    }
                  />
                </label>

                <label>
                  Price (₹)
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Leave empty for contact us"
                    value={course.price}
                    onChange={(event) =>
                      updateField("price", event.target.value)
                    }
                  />
                </label>
                <label className="cm-full">
                  Description
                  <textarea
                    rows={8}
                    value={course.description}
                    onChange={(event) =>
                      updateField("description", event.target.value)
                    }
                  />
                </label>

                <div className="cm-full">
                  <label>
                    Course Banner
                    <input
                      ref={fileInput}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.gif,.avif"
                      onChange={(event) => {
                        setBanner(event.target.files?.[0] || null);
                        setRemoveBanner(false);
                      }}
                    />
                  </label>

                  <small>Choose an image up to 5 MB.</small>

                  {image && (
                    <img
                      className="cm-banner"
                      src={image}
                      alt="Course banner preview"
                    />
                  )}

                  <div className="cm-actions">
                    {image && (
                      <button
                        type="button"
                        onClick={() => {
                          setBanner(null);
                          setRemoveBanner(true);
                          if (fileInput.current) {
                            fileInput.current.value = "";
                          }
                        }}
                      >
                        Remove Banner
                      </button>
                    )}

                    {removeBanner && course.image && (
                      <button
                        type="button"
                        onClick={() => setRemoveBanner(false)}
                      >
                        Undo Removal
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="cm-actions">
                {[
                  ["showTabs", "Show Horizontal Tabs"],
                  ["showSidebar", "Show Sidebar"],
                ].map(([key, label]) => (
                  <label className="cm-check" key={key}>
                    <input
                      type="checkbox"
                      checked={course.navigation[key]}
                      onChange={(event) =>
                        updateNavigation(key, event.target.checked)
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>

              <h3>Course Navigation Tabs</h3>
              <p>
                Enter folder paths separated by commas. Saving does not create
                image folders.
              </p>

              {course.navigation.tabs.map((tab, index) => (
                <div className="cm-tab" key={index}>
                  <div className="cm-grid">
                    <label>
                      Tab ID
                      <input
                        required
                        value={tab.id}
                        onChange={(event) =>
                          updateTab(index, "id", event.target.value)
                        }
                      />
                    </label>

                    <label>
                      Tab Title
                      <input
                        required
                        value={tab.title}
                        onChange={(event) =>
                          updateTab(index, "title", event.target.value)
                        }
                      />
                    </label>

                    <label>
                      Folders
                      <input
                        required
                        placeholder="01-tools, 02-punching/basic"
                        value={tab.folders.join(",")}
                        onChange={(event) =>
                          updateTab(
                            index,
                            "folders",
                            event.target.value.split(","),
                          )
                        }
                      />
                    </label>
                    <div className="cm-actions">
                      <button
                        type="button"
                        aria-label="Move tab up"
                        disabled={index === 0}
                        onClick={() => moveTab(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label="Move tab down"
                        disabled={index === course.navigation.tabs.length - 1}
                        onClick={() => moveTab(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="cm-remove-tab"
                        title="Remove tab"
                        aria-label="Remove tab"
                        onClick={() =>
                          updateNavigation(
                            "tabs",
                            course.navigation.tabs.filter(
                              (_, position) => position !== index,
                            ),
                          )
                        }
                      >
                        <i className="bi bi-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  updateNavigation("tabs", [
                    ...course.navigation.tabs,
                    { id: "", title: "", folders: [] },
                  ])
                }
              >
                + Add Tab
              </button>

              <div className="cm-actions">
                <button type="submit" className="cm-primary">
                  {busy ? "Processing…" : "Save Course"}
                </button>

                {originalId && (
                  <button type="button" onClick={() => send("delete")}>
                    Delete Course
                  </button>
                )}
              </div>

              <details>
                <summary>Preview Course JSON</summary>
                <pre>{JSON.stringify(payloadCourse(), null, 2)}</pre>
              </details>
            </form>
          </section>
        </div>
      </fieldset>
      <dialog
        ref={categoryDialog}
        className="cm-category-dialog"
        aria-labelledby="cm-category-dialog-title"
        onCancel={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            addCategory();
          }}
        >
          <div className="cm-category-dialog-header">
            <h2 id="cm-category-dialog-title">Add New Category</h2>

            <button
              type="button"
              className="cm-category-close"
              aria-label="Close category popup"
              disabled={busy}
              onClick={() => categoryDialog.current?.close()}
            >
              ×
            </button>
          </div>

          <p>Create a category for your courses.</p>

          <label htmlFor="cm-new-category">Category Name</label>

          <input
            id="cm-new-category"
            autoFocus
            required
            maxLength={100}
            placeholder="Enter category name"
            value={newCategory}
            disabled={busy}
            onChange={(event) => setNewCategory(event.target.value)}
          />

          {categoryError && (
            <p className="cm-category-error" role="alert">
              {categoryError}
            </p>
          )}

          <div className="cm-category-dialog-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => categoryDialog.current?.close()}
            >
              Cancel
            </button>

            <button type="submit" className="cm-primary" disabled={busy}>
              {busy ? "Adding…" : "Add Category"}
            </button>
          </div>
        </form>
      </dialog>
    </main>
  );
}
